import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  ApiError,
  Button,
  Eyebrow,
  Spinner,
  useConfirm,
  formatDate,
  formatDateTime,
  formatTime,
  money,
  timezoneDiffers,
  timezoneLabel,
} from '@busbook/ui';
import { useFeature, useTenant } from '../lib/tenant.js';
import { ticketPresentation } from '../lib/ticket-freshness.js';
import { api } from '../lib/api.js';
import { QrCode } from '../components/QrCode.js';
import { PushPrompt } from '../components/PushPrompt.js';
import { InstallPrompt } from '../components/InstallPrompt.js';
import { useT } from '../i18n/index.js';
import { PaymentStatus } from '../components/PaymentStatus.js';

/**
 * The ticket (docs/05 §5). This is the artifact a passenger shows at the gate, so
 * it is drawn as a physical stub — perforation, notches, QR below the fold — and
 * it renders from cache with no network.
 */

export interface BookingView {
  reference: string;
  status: string;
  totalCents: number;
  currency: string;
  refundedCents: number;
  contactName: string;
  contactEmail: string;
  confirmedAt: string | null;
  cancellationCutoffHours: number | null;
  trip: {
    id: string;
    status: string;
    delayMinutes: number;
    note: string | null;
    routeName: string;
    busName: string;
    departureAt: string;
    arrivalAt: string;
    fromStopName: string;
    fromStopCity: string;
    toStopName: string;
    toStopCity: string;
  };
  seats: Array<{
    seatCode: string;
    passengerName: string;
    priceCents: number;
    qrToken: string | null;
    checkedInAt: string | null;
  }>;
}

export function Ticket() {
  const t = useT();
  const { reference = '' } = useParams();
  const [params] = useSearchParams();
  const { settings, branding } = useTenant();

  const [booking, setBooking] = useState<BookingView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [servedAt, setServedAt] = useState<{ cached: boolean; at: string | null }>({
    cached: false,
    at: null,
  });
  const liveStatus = useFeature('liveStatus');
  const offlineTicket = useFeature('offlineTicket');
  const { ask, dialog } = useConfirm();

  // The one place offline-vs-live is decided; see ticket-freshness.ts for why.
  const presentation = ticketPresentation({
    cached: servedAt.cached,
    servedAt: servedAt.at,
    liveStatus,
    offlineTicket,
  });

  const token = params.get('token');
  // Set by the hosted payment provider's failure/cancel return URLs.
  const paymentOutcome = params.get('payment');

  const load = useCallback(async () => {
    try {
      const query = token ? `?token=${encodeURIComponent(token)}` : '';
      const { data, headers } = await api.requestDetailed<BookingView>(
        `/public/bookings/${reference}${query}`,
        // Sent with the bearer token when there is one. A magic-link token and the booking
        // session cookie both still work for a passenger who is not signed in; suppressing
        // the header only removed the third proof -- being the account that owns it -- which
        // is the one a passenger has on a phone that did not make the booking.
        { method: 'GET' },
      );
      setBooking(data);
      // Stamped by the service worker when it fell back to cache. `fetch` resolves either
      // way and the body is identical, so this header is the only thing that distinguishes
      // a ticket loaded at the gate with no signal from one loaded a moment ago.
      setServedAt(
        headers.get('x-busbook-cached') === '1'
          ? { cached: true, at: headers.get('date') }
          : { cached: false, at: null },
      );
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'NOT_FOUND'
          ? 'We could not find that booking on this device. Use the link from your confirmation email.'
          : 'Could not load your ticket.',
      );
    }
  }, [reference, token]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * After a hosted payment the passenger often lands here before the provider's
   * webhook has reached us, so a pending ticket polls until it resolves. It stops
   * on its own — an unpaid booking is expired by the seat-release job.
   */
  useEffect(() => {
    if (booking?.status !== 'PENDING_PAYMENT') return;
    const timer = setInterval(() => void load(), 3000);
    return () => clearInterval(timer);
  }, [booking?.status, load]);

  async function cancel() {
    if (!booking) return;
    const confirmed = await ask({
      title: `Cancel booking ${booking.reference}?`,
      body: 'Your seats will be released and you will be refunded.',
      confirmLabel: 'Cancel booking',
      cancelLabel: 'Keep booking',
    });
    if (!confirmed) return;

    setCancelling(true);
    setError(null);
    try {
      const query = token ? `?token=${encodeURIComponent(token)}` : '';
      setBooking(
        await api.post<BookingView>(`/public/bookings/${reference}/cancel${query}`),
      );
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'CANCELLATION_WINDOW_PASSED'
          ? err.message
          : 'We could not cancel this booking. Please contact support.',
      );
    } finally {
      setCancelling(false);
    }
  }

  if (error && !booking) {
    return (
      <section className="px-4 py-8">
        <Alert tone="error">{error}</Alert>
      </section>
    );
  }

  if (!booking) {
    return (
      <div className="grid place-items-center py-20">
        <Spinner label={t('t.loading')} />
      </div>
    );
  }

  const tz = settings.timezone;
  const showTzLabel = timezoneDiffers(tz);
  const cancelled = booking.status === 'CANCELLED' || booking.status === 'REFUNDED';
  const canCancel = booking.status === 'CONFIRMED' && booking.trip.status !== 'CANCELLED';

  /**
   * While a booking is unpaid the outcome IS the page.
   *
   * There is no boarding code to show yet, so rendering a ticket underneath a
   * banner puts two things on screen and answers neither clearly. The one
   * question a passenger has here is whether their money moved.
   */
  if (booking.status === 'PENDING_PAYMENT') {
    const settled = paymentOutcome === 'failed' || paymentOutcome === 'cancelled';
    return (
      <PaymentStatus
        tone={paymentOutcome === 'failed' ? 'failed' : paymentOutcome === 'cancelled' ? 'cancelled' : 'waiting'}
        title={
          paymentOutcome === 'failed'
            ? t('t.payFailed')
            : paymentOutcome === 'cancelled'
              ? t('t.payCancelled')
              : t('k.confirming')
        }
        detail={
          paymentOutcome === 'failed'
            ? t('t.stillHeld')
            : paymentOutcome === 'cancelled'
              ? t('t.heldLonger')
              : t('ps.closeSafe')
        }
        amountCents={booking.totalCents}
        currency={booking.currency}
        reference={booking.reference}
      >
        {/* Navigation only on a settled outcome. Offering a way out while a
            payment is still confirming invites someone to leave at the one
            moment they do not want to. */}
        {settled && (
          <Link
            to="/trips"
            className="text-sm font-medium underline underline-offset-4"
            style={{ color: 'var(--color-primary)' }}
          >
            {t('c.myTrips')}
          </Link>
        )}
      </PaymentStatus>
    );
  }

  return (
    <section className="px-4 py-6">

      {/*
        A ticket served from cache says so, and says when.
        The QR, the seats and the route are settled facts and are safe to show forever.
        The trip's STATUS is not: it is the one field that changes after the booking is
        made and the one a passenger acts on, so presenting a cached "on time" as current
        is the failure mode worth designing against. Hence a stamp rather than silence.
      */}
      {presentation.notice !== 'none' && (
        <div className="mb-4">
          <Alert tone="warning" title={t('t.offlineTicket')}>
            {presentation.notice === 'asOf' && servedAt.at
              ? t('t.offlineAsOf').replace('{when}', formatDateTime(servedAt.at, settings.timezone))
              : t('t.offlineNoStatus')}
          </Alert>
        </div>
      )}

      {/*
        Status alerts are suppressed entirely on a cached load. A stale "delayed by 20
        minutes" is not merely unhelpful — it is a specific claim about right now that
        nobody checked. The notice above already tells the passenger what is and is not
        known; repeating a two-day-old delay under it would undo that.
      */}
      {presentation.showStatus && (
        <>
          {booking.trip.status === 'CANCELLED' && (
            <div className="mb-4">
              <Alert tone="error" title={t('t.tripCancelled')}>
                {branding.appName} has cancelled this service. Your refund is on its way.
              </Alert>
            </div>
          )}
          {booking.trip.status !== 'CANCELLED' && booking.trip.delayMinutes > 0 && (
            <div className="mb-4">
              <Alert tone="warning" title={`Delayed by ${booking.trip.delayMinutes} minutes`}>
                {t('t.delayIncluded')}
              </Alert>
            </div>
          )}
          {booking.trip.note && (
            <div className="mb-4">
              <Alert tone="info">{booking.trip.note}</Alert>
            </div>
          )}
        </>
      )}
      {cancelled && (
        <div className="mb-4">
          <Alert tone="info" title={t('t.bookingCancelled')}>
            {booking.refundedCents > 0
              ? `Refunded ${money(booking.refundedCents, booking.currency)}.`
              : 'This booking is no longer valid.'}
          </Alert>
        </div>
      )}

      {/* The stub. One physical object: journey above the perforation, the
          scannable half below it. */}
      <article className="ticket" style={{ opacity: cancelled ? 0.55 : 1 }}>
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Eyebrow>{booking.trip.routeName}</Eyebrow>
              <p className="mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>
                {formatDate(booking.trip.departureAt, tz)}
              </p>
            </div>
            <p className="tabular text-right text-xs font-semibold" style={{ color: 'var(--color-muted)' }}>
              {booking.reference}
            </p>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <div>
              <time className="board-time block" dateTime={booking.trip.departureAt}>
                {formatTime(booking.trip.departureAt, tz)}
              </time>
              <p className="mt-1 text-xs font-medium">{booking.trip.fromStopName}</p>
              <p className="text-xs" style={{ color: 'var(--color-faint)' }}>
                {booking.trip.fromStopCity}
              </p>
            </div>
            <span className="rail mt-[-1.75rem]" aria-hidden />
            <div className="text-right">
              <time className="board-time block" dateTime={booking.trip.arrivalAt}>
                {formatTime(booking.trip.arrivalAt, tz)}
              </time>
              <p className="mt-1 text-xs font-medium">{booking.trip.toStopName}</p>
              <p className="text-xs" style={{ color: 'var(--color-faint)' }}>
                {booking.trip.toStopCity}
              </p>
            </div>
          </div>

          {showTzLabel && (
            <p className="mt-2 text-xs" style={{ color: 'var(--signal-delayed)' }}>
              {t('t.timesLocal', { tz: timezoneLabel(new Date(booking.trip.departureAt), tz) })}
            </p>
          )}

          <dl className="mt-4 grid grid-cols-3 gap-3 text-xs">
            <div>
              <dt style={{ color: 'var(--color-faint)' }}>{t('c.seats')}</dt>
              <dd className="tabular mt-0.5 font-semibold">
                {booking.seats.map((seat) => seat.seatCode).join(', ')}
              </dd>
            </div>
            <div>
              <dt style={{ color: 'var(--color-faint)' }}>Bus</dt>
              <dd className="mt-0.5 font-semibold">{booking.trip.busName}</dd>
            </div>
            <div>
              <dt style={{ color: 'var(--color-faint)' }}>{t('c.paid')}</dt>
              <dd className="money mt-0.5 font-semibold">
                {money(booking.totalCents, booking.currency)}
              </dd>
            </div>
          </dl>
        </div>

        <div className="ticket-perforation">
          <span className="ticket-notch-left" aria-hidden />
          <span className="ticket-notch-right" aria-hidden />
        </div>

        <div className="flex flex-col gap-6 p-5">
          {booking.seats.map((seat) => (
            <div key={seat.seatCode} className="flex flex-col items-center gap-2">
              <p className="text-sm font-semibold">
                {seat.passengerName}
                <span className="tabular" style={{ color: 'var(--color-muted)' }}>
                  {' '}· seat {seat.seatCode}
                </span>
              </p>
              {seat.qrToken && !presentation.showCode ? (
                /*
                 * The operator has offline tickets switched off, and this load came from
                 * cache. Showing the code anyway would be the feature they turned off,
                 * delivered by the service worker instead of by us — so the passenger is
                 * told plainly why it is not here rather than shown a code the operator
                 * does not want boarded on.
                 */
                <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                  {t('t.offlineCodeHidden')}
                </p>
              ) : seat.qrToken ? (
                <>
                  <div className="rounded-lg bg-white p-2">
                    <QrCode
                      value={seat.qrToken}
                      size={176}
                      label={`Boarding code for ${seat.passengerName}, seat ${seat.seatCode}`}
                    />
                  </div>
                  {seat.checkedInAt && (
                    <p className="text-xs font-medium" style={{ color: 'var(--signal-departed)' }}>
                      Checked in at {formatTime(seat.checkedInAt, tz)}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                  {t('t.codeAfterPay')}
                </p>
              )}
            </div>
          ))}
        </div>
      </article>

      {error && (
        <div className="mt-4">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      {booking.status === 'CONFIRMED' && (
        <div className="mt-5">
          <PushPrompt appName={branding.appName} />
          {/* A ticket is the thing worth having on the home screen, and the thing
              worth having offline: bus stations are where signal runs out. */}
          <InstallPrompt appName={branding.appName} />
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3">
        {canCancel && (
          <Button variant="secondary" block loading={cancelling} onClick={cancel}>
            {t('t.cancelBooking')}
          </Button>
        )}
        {booking.status === 'CONFIRMED' && !canCancel && (
          <p className="text-center text-xs" style={{ color: 'var(--color-muted)' }}>
            {t('t.noLongerCancel')}
          </p>
        )}
        <Link
          to={`/bookings/${booking.reference}/dispute`}
          className="text-center text-sm underline underline-offset-4"
          style={{ color: 'var(--color-primary)' }}
        >
          {t('t.reportProblem')}
        </Link>
      </div>
      {dialog}
    </section>
  );
}
